import React, { FC, useMemo } from 'react';
import { achievements } from '../../utils/data';

interface Achievement {
  id: number;
  date: string;
  title: string;
  organization: string;
  description: string;
}

const Achievements: FC = () => {
  // Check if achievements data exists and is an array
  const achievementsData = useMemo(() => {
    return Array.isArray(achievements) ? achievements : [];
  }, []);

  return (
    <section id="achievements" className="achievements-section" aria-labelledby="achievements-heading">
      <div className="achievements-content">
        <h2 id="achievements-heading" className="section-title">Achievements</h2>
        
        {achievementsData.length > 0 ? (
          <div className="achievements-timeline" role="list">
            {achievementsData.map((achievement: Achievement) => (
              <div className="achievement-item" key={achievement.id} role="listitem">
                <div className="achievement-marker" aria-hidden="true"></div>
                <div className="achievement-card">
                  <div className="achievement-date">{achievement.date}</div>
                  <h3 className="achievement-title">{achievement.title}</h3>
                  <div className="achievement-org">{achievement.organization}</div>
                  <p className="achievement-desc">{achievement.description}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="no-achievements">No achievements to display</p>
        )}
      </div>
      
      <style>
        {`
        .achievements-section {
          min-height: 100vh;
          width: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 80px 0;
          scroll-margin-top: 60px; /* For smooth scrolling to this section */
        }
        
        .achievements-content {
          width: 100%;
          max-width: 1000px;
          padding: 0 20px;
        }
        
        .section-title {
          font-size: 42px;
          color: #00ff00;
          margin-bottom: 40px;
          text-align: center;
          text-shadow: 0 0 10px rgba(0, 255, 0, 0.7);
          font-family: 'VT323', monospace;
          letter-spacing: 3px;
        }
        
        .achievements-timeline {
          position: relative;
          padding: 30px 0;
        }
        
        .achievements-timeline::before {
          content: '';
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          width: 2px;
          height: 100%;
          background-color: #00ff00;
          box-shadow: 0 0 8px #00ff00;
        }
        
        .achievement-item {
          position: relative;
          margin-bottom: 50px;
          display: flex;
          justify-content: center;
          align-items: center;
        }
        
        .achievement-marker {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background-color: #00ff00;
          box-shadow: 0 0 10px #00ff00;
          z-index: 2;
        }
        
        .achievement-card {
          width: calc(50% - 40px);
          background-color: rgba(0, 0, 0, 0.7);
          border: 1px solid #00ff00;
          border-radius: 5px;
          padding: 20px;
          position: relative;
          box-shadow: 0 0 15px rgba(0, 255, 0, 0.3);
          transition: all 0.3s ease;
        }
        
        .achievement-item:nth-child(odd) .achievement-card {
          margin-right: 60px;
        }
        
        .achievement-item:nth-child(even) .achievement-card {
          margin-left: 60px;
        }
        
        .achievement-card::after {
          content: '';
          position: absolute;
          top: 50%;
          width: 20px;
          height: 2px;
          background-color: #00ff00;
          box-shadow: 0 0 5px #00ff00;
        }
        
        .achievement-item:nth-child(odd) .achievement-card::after {
          right: -20px;
        }
        
        .achievement-item:nth-child(even) .achievement-card::after {
          left: -20px;
        }
        
        .achievement-card:hover {
          transform: scale(1.03);
          box-shadow: 0 0 20px rgba(0, 255, 0, 0.6);
        }
        
        .achievement-date {
          color: #00ff00;
          font-size: 16px;
          opacity: 0.8;
          margin-bottom: 10px;
        }
        
        .achievement-title {
          color: #00ff00;
          font-size: 20px;
          font-family: 'VT323', monospace;
          margin-bottom: 10px;
          letter-spacing: 1px;
          font-weight: normal; /* Ensure consistent heading weight */
        }
        
        .achievement-org {
          color: #00ff00;
          font-size: 16px;
          margin-bottom: 10px;
          font-style: italic;
          opacity: 0.9;
        }
        
        .achievement-desc {
          color: #00ff00;
          font-size: 14px;
          line-height: 1.5;
        }
        
        .no-achievements {
          color: #00ff00;
          text-align: center;
          font-size: 18px;
          padding: 30px;
          border: 1px dashed #00ff00;
          border-radius: 5px;
        }
        
        @media (max-width: 768px) {
          .achievements-timeline::before {
            left: 20px;
          }
          
          .achievement-marker {
            left: 20px;
          }
          
          .achievement-card {
            width: calc(100% - 60px);
            margin-left: 40px !important;
            margin-right: 0 !important;
          }
          
          .achievement-item:nth-child(odd) .achievement-card::after,
          .achievement-item:nth-child(even) .achievement-card::after {
            left: -20px;
            right: auto;
          }
          
          .section-title {
            font-size: 32px; /* Smaller title on mobile */
          }
        }
        `}
      </style>
    </section>
  );
};

export default Achievements;